'use client';
import { useInViewport, useMemoizedFn } from 'ahooks';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { Button } from '@/app/components/ui/button';
import { ScreenType } from '@/app/constants';
import { type ScreenConfigType, useExpose, useScreenConfig } from '@/app/hooks';
import { cn } from '@/app/utils';

export enum SCREEN_WIDTH_MAP {
  /** 10个半 */
  TEN_AND_A_HALF = 'w-[calc(9.52%-11.4px)]',
  /** 8个半 */
  EIGHT_AND_A_HALF = 'w-[calc(11.8%-11.3px)]',
  /** 6个半 */
  SIX_AND_A_HALF = 'w-[calc(15.38%-11.1px)]',
  /** 5个半 */
  FIVE_AND_A_HALF = 'w-[calc(18.2%-11px)]',
  /** 4个半 */
  FOUR_AND_A_HALF = 'w-[calc(22%-10.7px)]',
  /** 3个半 */
  THREE_AND_A_HALF = 'w-[calc(28.6%-10.3px)]',
  /** 2个半 */
  TWO_AND_A_HALF = 'w-[calc(40%-6.4px)]',
}

const DefaultWidthClassMap: ScreenConfigType<SCREEN_WIDTH_MAP> = {
  [ScreenType.QHD_PLUS]: SCREEN_WIDTH_MAP.TEN_AND_A_HALF,
  [ScreenType.QHD]: SCREEN_WIDTH_MAP.EIGHT_AND_A_HALF,
  [ScreenType.DEFAULT]: SCREEN_WIDTH_MAP.FIVE_AND_A_HALF,
  [ScreenType.LG]: SCREEN_WIDTH_MAP.FOUR_AND_A_HALF,
  [ScreenType.MD]: SCREEN_WIDTH_MAP.THREE_AND_A_HALF,
  [ScreenType.SM]: SCREEN_WIDTH_MAP.TWO_AND_A_HALF,
  placeholder: SCREEN_WIDTH_MAP.FOUR_AND_A_HALF,
};

interface HorizontalCarouselProps<ItemType> {
  list: ItemType[];
  widthClassMap?: ScreenConfigType<SCREEN_WIDTH_MAP>;
  onItemExpose: (idx: number) => void;
  renderItem: (item: ItemType) => ReactNode;
  onNext?: (idx: number) => void;
  showScrollButtons?: boolean;
}

const HorizontalCarousel = <ItemType extends { id?: string }>({
  list,
  onItemExpose,
  renderItem,
  onNext,
  widthClassMap = DefaultWidthClassMap,
  showScrollButtons = true,
}: HorizontalCarouselProps<ItemType>) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);
  const [enableScroll, setEnableScroll] = useState(false);

  const columnNum = useScreenConfig<SCREEN_WIDTH_MAP>(widthClassMap);

  const checkScrollPosition = () => {
    if (!scrollContainerRef.current) return;

    setEnableScroll(scrollContainerRef.current.scrollWidth !== scrollContainerRef.current.clientWidth);
    const container = scrollContainerRef.current;
    const isAtLeft = container.scrollLeft <= 0;
    const isAtRight = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1; // -1 for potential rounding errors

    setIsLeftDisabled(isAtLeft);
    setIsRightDisabled(isAtRight);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScrollPosition();
      container.addEventListener('scroll', checkScrollPosition);

      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, []);

  useEffect(() => {
    checkScrollPosition();
  }, [list, columnNum]);

  const scrollToLeft = () => {
    if (!scrollContainerRef.current || isLeftDisabled) return;
    const container = scrollContainerRef.current;
    const { children, clientWidth } = scrollContainerRef.current;
    const nextFirstIndex = visibleItems.current.sort((a, b) => a - b)[0] + 1;
    const { offsetLeft } = children[nextFirstIndex] as HTMLElement;
    const targetScrollPosition = offsetLeft - clientWidth - 12;
    if (targetScrollPosition < 12 + itemsWidthRef.current) {
      container.scrollTo({
        behavior: 'smooth',
        left: 0,
      });
    } else {
      container.scrollTo({
        behavior: 'smooth',
        left: targetScrollPosition,
      });
    }
  };

  const scrollToRight = () => {
    if (!scrollContainerRef.current || isRightDisabled) return;
    const { children, clientWidth, scrollWidth } = scrollContainerRef.current;
    const container = scrollContainerRef.current;
    const nextFirstIndex = visibleItems.current.sort((a, b) => a - b).slice(-1)[0];
    const { offsetLeft } = children[nextFirstIndex] as HTMLElement;
    const targetScrollPosition = offsetLeft;
    if (targetScrollPosition + clientWidth + itemsWidthRef.current + 12 >= scrollWidth) {
      container.scrollTo({
        behavior: 'smooth',
        left: scrollWidth,
      });
    } else {
      container.scrollTo({
        behavior: 'smooth',
        left: targetScrollPosition,
      });
    }
    onNext?.(nextFirstIndex);
  };

  const itemListRef = useRef<HTMLDivElement[]>([]);
  const visibleItems = useRef<number[]>([]);
  const itemsWidthRef = useRef<number>(0);

  const itemScrollBack = useMemoizedFn((entry) => {
    if (entry.isIntersecting) {
      if (!itemsWidthRef.current) {
        itemsWidthRef.current = entry.target.clientWidth;
      }
      const active = Number(entry.target.getAttribute('data-index'));
      visibleItems.current.push(active);
    } else {
      const inActive = Number(entry.target.getAttribute('data-index'));
      visibleItems.current = visibleItems.current.filter((it) => it !== inActive);
    }
  });

  useInViewport(itemListRef.current, {
    callback: itemScrollBack,
    root: () => scrollContainerRef.current,
    threshold: 0.1,
  });

  useExpose(
    (idx) => {
      onItemExpose?.(idx);
    },
    itemListRef.current,
    list,
  );

  return (
    <div className="relative">
      <div ref={scrollContainerRef} className={cn('hide-scrollbar flex w-full gap-3 overflow-x-scroll')}>
        {list.map((item, index) => (
          <div
            ref={(el: HTMLDivElement) => {
              itemListRef.current[index] = el;
            }}
            className={cn('flex-shrink-0', columnNum)}
            key={`horizontal-carousel-item-${index + 1}`}
            data-index={index}
          >
            {renderItem(item)}
          </div>
        ))}
      </div>
      {showScrollButtons && enableScroll && list.length > 0 && (
        <Button
          className="-translate-y-1/2 group absolute top-[50%] bottom-0 left-2 z-[15] flex h-9 w-9 items-center justify-center rounded-8 border border-system-white10 bg-system-white10 p-0 backdrop-blur-[20px] hover:bg-system-white20 disabled:hover:bg-system-white10 sm:hidden"
          disabled={isLeftDisabled}
          onClick={scrollToLeft}
        >
          <BaseLineLeft className="size-5 text-system-white group-disabled:text-system-white20" />
        </Button>
      )}
      {showScrollButtons && enableScroll && list.length > 0 && (
        <Button
          className="-translate-y-1/2 group absolute top-[50%] right-2 bottom-0 z-[15] flex h-9 w-9 items-center justify-center rounded-8 border border-system-white10 bg-system-white10 p-0 backdrop-blur-[20px] hover:bg-system-white20 disabled:hover:bg-system-white10 sm:hidden"
          disabled={isRightDisabled}
          onClick={scrollToRight}
        >
          <BaseLineRight className="size-5 text-system-white group-disabled:text-system-white20" />
        </Button>
      )}
    </div>
  );
};

export default HorizontalCarousel;
